import { z } from 'zod';
import { personNameSchema } from './person-name.js';
import { AppointmentModeSchema } from '../enums/appointment-mode.js';
import { ConsentStatusSchema } from '../enums/consent-status.js';
import {
  CancellationReasonSchema,
  DeliveryFailedReasonSchema,
  LeadStatusSchema,
  NonVenteReasonSchema,
} from '../enums/lead-status.js';
import { LeadTelcoSaleSchema } from '../enums/telco.js';
import { EnergySaleTypeSchema } from '../enums/energy.js';
import { QualificationCategorySchema } from '../enums/qualification-category.js';
import { FrenchPostalCodeSchema, IsoDateStringSchema, UuidV7Schema } from './common.js';
import { LeadConsentInputSchema } from './lead-consent.js';
import { isValidE164FrenchMobile, toE164French } from './phone.js';

/**
 * Phone d'output (lecture) : doit être E.164 mobile FR (`+33[67]XXXXXXXX`).
 * Si une donnée legacy non conforme passait, on accepte une string brute
 * 6-30 chars en fallback pour ne pas crasher (la ligne sera juste affichée
 * telle quelle côté UI).
 */
const PhoneOutputSchema = z.string().min(6).max(30);

/**
 * Phone d'input (création/édition) : `z.preprocess` normalise toute saisie
 * locale (`0612345678` / `06 12 34 56 78`) ou internationale vers E.164,
 * puis valide. Refuse explicitement les non-mobile FR pour les *nouvelles*
 * saisies (legacy reste tolérée à la lecture via PhoneOutputSchema).
 */
const PhoneInputSchema = z.preprocess(
  (v) => (typeof v === 'string' ? toE164French(v) : v),
  z.string().refine(isValidE164FrenchMobile, {
    message: 'Numéro mobile FR invalide (attendu : 06… ou 07…)',
  }),
);

export const LeadSchema = z.object({
  id: UuidV7Schema,
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email(),
  phone: PhoneOutputSchema,
  address: z.string().max(255).nullable().optional(),
  postalCode: FrenchPostalCodeSchema,
  city: z.string().max(100).nullable().optional(),
  partnerId: UuidV7Schema,
  commercialId: UuidV7Schema,
  /**
   * Magasin où la fiche a été faite (réseaux de boutiques), FIGÉ à la
   * création ; seul le back-office le change (`PATCH /leads/:id/attribution`).
   * Null en porte-à-porte et sur les fiches antérieures aux magasins.
   */
  storeId: UuidV7Schema.nullable().optional(),
  store: z.object({ id: UuidV7Schema, name: z.string() }).nullable().optional(),
  status: LeadStatusSchema,
  /**
   * Raison de la non-vente, motivée côté livraison (Hopti). Toujours `null`
   * tant que `status !== 'not_sold'`. **Admin-only** côté API : retiré par
   * AnonymizeInterceptor pour les autres rôles.
   */
  nonVenteReason: NonVenteReasonSchema.nullable().optional(),
  /** Raison d'annulation. Null hors status=`cancelled`. Admin-only. */
  cancellationReason: CancellationReasonSchema.nullable().optional(),
  /** Raison d'échec de livraison. Null hors status=`delivery_failed`. Admin-only. */
  deliveryFailedReason: DeliveryFailedReasonSchema.nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  /**
   * Statut RGPD du consentement SMS. `verified` = livraison normale ;
   * `pending_offline` = SMS différé après création hors-ligne, le vendeur
   * doit saisir le code dans le délai imparti ; `expired` = délais
   * dépassés, lead non transmis.
   */
  consentStatus: ConsentStatusSchema.optional(),
  /** Deadline pour la saisie du code (ISO). Null hors mode différé. */
  consentExpiresAt: IsoDateStringSchema.nullable().optional(),
  /** SmsLog id du SMS de consentement différé. Null si verified live. */
  consentSmsLogId: UuidV7Schema.nullable().optional(),
  /**
   * Mode de rendez-vous capturé dans le wizard. `null` pour les leads
   * créés hors wizard (admin manuel) ou legacy avant la promotion.
   */
  appointmentMode: AppointmentModeSchema.nullable().optional(),
  /** Datetime du rappel planifié (UTC). Null si mode != scheduled. */
  scheduledFor: IsoDateStringSchema.nullable().optional(),
  version: z.number().int().min(0),
  archived: z.boolean(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
  /**
   * Réconciliation TMK (verticale Telco livrée au Lab). Compteurs de
   * synthèse fibre/mobile + détail des ventes rattachées (relation
   * 1-to-many : 1 box fibre + N lignes mobile possibles). `null`/absent
   * tant que le lead n'a jamais été réconcilié ou hors verticale Telco.
   */
  telcoFibreCount: z.number().int().nonnegative().nullable().optional(),
  telcoMobileCount: z.number().int().nonnegative().nullable().optional(),
  telcoEnrichedAt: IsoDateStringSchema.nullable().optional(),
  telcoSales: z.array(LeadTelcoSaleSchema).optional(),
  /**
   * Type de vente Énergie (Active Contact) dérivé du `raw_label` de la
   * qualif de vente (`ACCORDS ELEC/GAZ/DUAL`) à l'ingestion. Pendant
   * "énergie" des compteurs TMK Telco, mais sans réconciliation externe.
   * `null`/absent hors verticale énergie ou partenaire ≠ Active Contact.
   */
  energySaleType: EnergySaleTypeSchema.nullable().optional(),
});
export type Lead = z.infer<typeof LeadSchema>;

export const CreateLeadSchema = z.object({
  id: UuidV7Schema.optional(),
  firstName: personNameSchema({ min: 1, max: 100 }),
  lastName: personNameSchema({ min: 1, max: 100 }),
  email: z.string().email(),
  phone: PhoneInputSchema,
  address: z.string().max(255).nullable().optional(),
  postalCode: FrenchPostalCodeSchema,
  city: z.string().max(100).nullable().optional(),
  partnerId: UuidV7Schema.nullable().optional(),
  commercialId: UuidV7Schema.nullable().optional(),
  // V2 fields
  verticaleId: UuidV7Schema.nullable().optional(),
  deliveryPartnerId: UuidV7Schema.nullable().optional(),
  specificData: z.record(z.unknown()).nullable().optional(),
  contactGroupId: UuidV7Schema.nullable().optional(),
  civilite: z.string().max(10).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  // BAN (Base Adresse Nationale) — résolus quand le wizard pousse une
  // suggestion choisie ; null si saisie libre.
  addrBanId: z.string().max(64).nullable().optional(),
  // Code INSEE de commune : 5 chiffres, ou 2A/2B + 3 chiffres pour la Corse
  codeInsee: z
    .string()
    .regex(/^(\d{5}|2[AB]\d{3})$/i)
    .nullable()
    .optional(),
  addrLat: z.number().min(-90).max(90).nullable().optional(),
  addrLon: z.number().min(-180).max(180).nullable().optional(),
  /**
   * Mode différé hors-ligne : le vendeur a validé le wizard sans réseau,
   * le SMS sera envoyé par le serveur à la création du lead. Default
   * `verified` (cas online classique). Si `pending_offline`, le serveur
   * envoie un SMS avec TTL 30 min, set `consentExpiresAt = createdAt + 1h`,
   * et **ne déclenche pas** la livraison Collect/Hopti tant que le code
   * n'a pas été validé via `POST /leads/:id/verify-consent`.
   */
  consentStatus: ConsentStatusSchema.optional(),
  /**
   * Mode de rendez-vous capturé dans le wizard. `immediate` = rappel
   * direct (gaté horaires partenaire) ; `scheduled` = rappel planifié à
   * `scheduledFor`. Optionnel pour les chemins de création hors wizard.
   */
  appointmentMode: AppointmentModeSchema.nullable().optional(),
  /** Datetime du rappel planifié (ISO UTC). Requis si appointmentMode='scheduled'. */
  scheduledFor: IsoDateStringSchema.nullable().optional(),
  /**
   * Audit RGPD : labels des checkboxes cochées par le prospect, dans
   * l'ordre d'affichage. Persistés en table `lead_consents`. Optionnel
   * (l'admin qui crée un lead manuellement n'a pas de wizard).
   */
  consents: z.array(LeadConsentInputSchema).max(20).optional(),
  /**
   * Magasin choisi sur la tablette AU MOMENT de la saisie (réseaux de
   * boutiques). Voyage dans la file hors ligne : la fiche peut partir des
   * heures plus tard. Indicatif — l'API le revérifie contre les affectations
   * de l'utilisateur de l'appareil et l'ignore s'il ne tient pas, sans
   * jamais rejeter la fiche pour autant (cf. `StoresService.resolveForLead`).
   */
  storeId: UuidV7Schema.nullable().optional(),
});
export type CreateLeadInput = z.infer<typeof CreateLeadSchema>;

/** Body de `POST /leads/:id/verify-consent`. */
export const VerifyLeadConsentSchema = z.object({
  code: z.string().regex(/^\d{4}$/, 'Code à 4 chiffres attendu'),
});
export type VerifyLeadConsentInput = z.infer<typeof VerifyLeadConsentSchema>;

export const VerifyLeadConsentResponseSchema = z.object({
  verified: z.boolean(),
  /** Présent quand verified=false : nombre d'essais restants. */
  attemptsLeft: z.number().int().min(0).optional(),
  /** Présent quand verified=false : true si c'est un cas d'expiration
      définitive (lead.consentExpiresAt dépassé OU SmsLog expiré). */
  expired: z.boolean().optional(),
  /** Présent quand verified=true : le lead à jour avec consentStatus
      verified, prêt pour la livraison. */
  lead: LeadSchema.optional(),
});
export type VerifyLeadConsentResponse = z.infer<typeof VerifyLeadConsentResponseSchema>;

/** Body de `POST /leads/:id/resend-consent-otp`. */
export const ResendConsentOtpResponseSchema = z.object({
  smsLogId: UuidV7Schema,
  /** ISO du sentAt du nouvel SmsLog (= base du TTL 30 min). */
  sentAt: IsoDateStringSchema,
});
export type ResendConsentOtpResponse = z.infer<typeof ResendConsentOtpResponseSchema>;

// Le magasin ne s'édite pas par `PATCH /leads/:id` : il est figé à la
// création, seul le back-office le change (`PATCH /leads/:id/attribution`).
export const UpdateLeadSchema = CreateLeadSchema.omit({ storeId: true }).partial().extend({
  version: z.number().int().min(0),
  status: LeadStatusSchema.optional(),
});
export type UpdateLeadInput = z.infer<typeof UpdateLeadSchema>;

/**
 * Body de `POST /leads/:id/sale-confirmation` (admin) : confirme ou refuse
 * une vente `sale_validated`. C'est la source de confirmation MANUELLE (pour
 * les verticales sans rappro automatique + override telco). `confirm` →
 * `sale_confirmed` (commission), `reject` → `sale_rejected`.
 */
export const SaleConfirmationSchema = z.object({
  decision: z.enum(['confirm', 'reject']),
  note: z.string().max(500).optional(),
});
export type SaleConfirmation = z.infer<typeof SaleConfirmationSchema>;

/**
 * Body de `POST /leads/:id/admin-qualification` (admin) : ajoute une
 * qualification « administrative » sur une fiche, quel que soit son statut —
 * pour les cas où aucune qualif du centre d'appel n'arrivera (partenaire sans
 * param, doublon refusé par le WS de l'acheteur…).
 * `category` = la catégorie enregistrée, visible du vendeur. Mêmes règles de
 * statut que le picker `categorize` : `sold` → `sale_validated`, `not_sold` →
 * `not_sold`, le reste ne touche pas au statut (garde « statut terminal »).
 * `closeAsNotSold` (case à cocher, découplée) → clôture la fiche en
 * `not_sold` (sur une fiche encore ouverte uniquement ; interdit avec `sold`).
 */
export const AdminQualificationSchema = z.object({
  category: QualificationCategorySchema,
  closeAsNotSold: z.boolean().optional().default(false),
  note: z.string().max(500).optional(),
});
export type AdminQualificationInput = z.infer<typeof AdminQualificationSchema>;

/** Taille maximale d'une page de `GET /leads`. Au-delà, le client pagine. */
export const LEADS_PAGE_MAX = 1000;

export const LeadFiltersSchema = z.object({
  status: LeadStatusSchema.optional(),
  partnerId: UuidV7Schema.optional(),
  commercialId: UuidV7Schema.optional(),
  managerId: UuidV7Schema.optional(),
  superviseurId: UuidV7Schema.optional(),
  /** Magasin de la fiche (réseaux de boutiques). */
  storeId: UuidV7Schema.optional(),
  search: z.string().max(100).optional(),
  from: IsoDateStringSchema.optional(),
  to: IsoDateStringSchema.optional(),
  /** Pagination par curseur : id (UUID v7, donc ordonné comme createdAt) de la
      dernière fiche de la page précédente. */
  cursor: UuidV7Schema.optional(),
  limit: z.coerce.number().int().min(1).max(LEADS_PAGE_MAX).optional(),
});
export type LeadFilters = z.infer<typeof LeadFiltersSchema>;
